/**
 * Issue #437 — SOC 2 trust-criteria scope picker.
 *
 * One screen: tick the criteria that apply. Each optional criterion carries
 * the question that decides it, so the guidance lives on the thing you are
 * choosing rather than on a separate quiz step that set the same checkboxes
 * indirectly. (The quiz that used to precede this screen was never
 * persisted — it derived this list and was re-inferred from it on resume,
 * so collapsing the two loses no stored data.)
 *
 * The persisted value is a plain `string[]` of TSC values, which is what
 * `mapOnboardingAnswers` and control-matrix seeding already consume.
 */
import { useEffect, useRef } from 'react'
import {
  CheckboxInput,
  type WizardOption,
} from '@/components/screenata/wizard-inputs'

/**
 * Shared with the auditor-center cycle dialog — the same decision is made
 * there, so the deciding question belongs on the option itself, once.
 *
 * `Processing Integrity` is the canonical space-form value: it must match
 * the server scope gate (TSC_CONTROL_PREFIXES in soc2-constants.ts) and the
 * audit-package `processing_integrity_in_scope` check. The old underscore
 * form left the criterion permanently out of scope server-side (#610).
 */
export const TSC_OPTIONS: WizardOption[] = [
  {
    value: 'Security',
    label: 'Security (required)',
    description: 'Common Criteria, always included in every SOC 2 report.',
    disabled: true,
  },
  {
    value: 'Availability',
    label: 'Availability',
    description:
      'Do your customer contracts commit to an uptime number? A real SLA: a promise like 99.9%, not "best effort."',
  },
  {
    value: 'Confidentiality',
    label: 'Confidentiality',
    description:
      'Do customer contracts require you to keep their data confidential? NDAs, confidentiality clauses, or an MSA that says customer data stays private. Most B2B SaaS has this.',
  },
  {
    value: 'Processing Integrity',
    label: 'Processing Integrity',
    description:
      'Does your product process payments or financial transactions? Transaction processing that has to be exactly right. If Stripe moves the money and you only read the result, leave this off.',
  },
  {
    value: 'Privacy',
    label: 'Privacy',
    description:
      'Do you hold personal data about individuals beyond basic work contact info? Home addresses, dates of birth, health or financial details. Work emails and login credentials do not count.',
  },
]

interface TrustCriteriaPickerInputProps {
  value: string[] | null
  onChange: (tscs: string[]) => void
}

export function TrustCriteriaPickerInput({
  value,
  onChange,
}: TrustCriteriaPickerInputProps) {
  const selected =
    Array.isArray(value) && value.length > 0 ? value : ['Security']

  // Security is in scope for every SOC 2 report, so seed it on arrival.
  // Without this the screen would render Security ticked (via the fallback
  // above) while Continue stayed disabled on an unset answer.
  const seededRef = useRef(false)
  useEffect(() => {
    if (seededRef.current) return
    seededRef.current = true
    if (!Array.isArray(value) || value.length === 0) onChange(['Security'])
  }, [onChange, value])

  return (
    <CheckboxInput
      options={TSC_OPTIONS}
      value={selected}
      onChange={(next) =>
        onChange(next.includes('Security') ? next : ['Security', ...next])
      }
    />
  )
}
